export type HttpErrorOptions = {
  method?: string;
  url?: string;
  requestId?: string;
  correlationId?: string;
  traceparent?: string;
  timestamp?: string;
};

export class HttpError extends Error {
  method?: string;
  url?: string;
  requestId?: string;
  correlationId?: string;
  traceparent?: string;
  timestamp?: string;

  constructor(
    public status: number,
    public statusText: string,
    public body: string,
    options: HttpErrorOptions = {}
  ) {
    super(`HTTP ${status} ${statusText}`);
    this.name = 'HttpError';
    this.method = options.method;
    this.url = options.url;
    this.requestId = options.requestId;
    this.correlationId = options.correlationId || options.requestId;
    this.traceparent = options.traceparent;
    this.timestamp = options.timestamp;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

export type GetTokenFn = (() => string | null | undefined) | (() => Promise<string | null | undefined>);
export type GetTenantFn = (() => string | null | undefined) | (() => Promise<string | null | undefined>);

export interface CreateClientOptions {
  baseUrl: string | (() => string);
  getToken?: GetTokenFn;
  getTenantId?: GetTenantFn;
  getRefreshToken?: GetTokenFn;
  disableLocalAuthFallback?: boolean;
  onTokensRefreshed?: (t: { accessToken: string; refreshToken?: string | null; expiresAt?: number | null; user?: unknown }) => void;
  onUnauthorized?: (reason: AuthLogoutReason, source?: string) => void;
  refreshPath?: string; // defaults to '/auth/refresh'
  timeoutMs?: number;
}

type Json = Record<string, unknown> | unknown[];

export type RequestOptions = {
  headers?: Record<string, string>;
  /** Pin queued operations to their original store, including authentication retries. */
  tenantId?: string;
  timeoutMs?: number;
  /** Cancel this operation, including waiting for a shared token refresh. */
  signal?: AbortSignal;
  /** Internal flag used to limit authentication retries to one attempt. */
  retried?: boolean;
};

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'HEAD' | 'DELETE';

// Canonical API prefix/version used by the backend. Keep the minor version to
// avoid ambiguity when a 1.1 is introduced; clients can still call a plain
// /api/... path and we'll rewrite to this prefix.
const API_PREFIX = '/api';
const VERSIONED_API_PREFIX = `/api`;

let lastLogoutDispatch: { reason: string; at: number } | null = null;

const DEFAULT_REQUEST_TIMEOUT_MS = 8000;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type AuthLogoutReason = 'session-expired' | 'idle-timeout' | 'manual' | 'unknown';

function dispatchAuthLogout(reason: AuthLogoutReason, source?: string) {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  // Coalesce bursty unauthorized events into a single redirect signal.
  if (lastLogoutDispatch && lastLogoutDispatch.reason === reason && now - lastLogoutDispatch.at < 1500) {
    return;
  }
  lastLogoutDispatch = { reason, at: now };
  try {
    window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason, source, at: now } }));
  } catch { }
}

async function maybe<T>(fn?: (() => T | null | undefined) | (() => Promise<T | null | undefined>)) {
  if (!fn) return null as T | null;
  return await fn();
}

function isUuid(value: string) {
  return UUID_REGEX.test(value);
}

function normalizePath(path: string): string {
  if (!path || path.startsWith('http://') || path.startsWith('https://')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === API_PREFIX || normalized.startsWith(`${API_PREFIX}/`)) return normalized;
  return `${API_PREFIX}${normalized}`;
}

function splitPathAndSuffix(path: string) {
  const suffixIndex = path.search(/[?#]/);
  if (suffixIndex < 0) {
    return { pathname: path, suffix: '' };
  }

  return {
    pathname: path.slice(0, suffixIndex),
    suffix: path.slice(suffixIndex),
  };
}

function joinUrlPath(basePath: string, path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const normalizedBase = basePath && basePath !== '/'
    ? `/${basePath.replace(/^\/+|\/+$/g, '')}`
    : '';

  if (!normalizedBase) return normalizedPath;
  if (normalizedPath === normalizedBase || normalizedPath.startsWith(`${normalizedBase}/`)) {
    return normalizedPath;
  }

  return `${normalizedBase}/${normalizedPath.replace(/^\/+/, '')}`;
}

function makeUrl(baseUrl: string, path: string) {
  if (!path || path.startsWith('http://') || path.startsWith('https://')) return path;

  const { pathname, suffix } = splitPathAndSuffix(path);
  const base = (baseUrl || '').trim();

  if (!base || base === '/') {
    const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return `${normalizedPath}${suffix}`;
  }

  if (base.startsWith('/')) {
    const { pathname: basePath } = splitPathAndSuffix(base);
    return `${joinUrlPath(basePath, pathname)}${suffix}`;
  }

  try {
    const url = new URL(base);
    url.pathname = joinUrlPath(url.pathname, pathname);
    url.search = '';
    url.hash = '';
    return `${url.toString()}${suffix}`;
  } catch {
    return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }
}

function createUnauthorizedError(message = 'Unauthorized') {
  const error = new Error(message) as Error & { status?: number; statusCode?: number; code?: string; name: string };
  error.name = 'UnauthorizedError';
  error.status = 401;
  error.statusCode = 401;
  error.code = 'AUTH_UNAUTHORIZED';
  return error;
}

function readDefaultTimeoutMs() {
  try {
    const env: any =
      typeof globalThis !== 'undefined' && (globalThis as any)?.process?.env ? (globalThis as any).process.env : {};
    const raw = env.NEXT_PUBLIC_API_REQUEST_TIMEOUT_MS || env.API_REQUEST_TIMEOUT_MS || env.NEXT_PUBLIC_STOREFRONT_REQUEST_TIMEOUT_MS;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : DEFAULT_REQUEST_TIMEOUT_MS;
  } catch {
    return DEFAULT_REQUEST_TIMEOUT_MS;
  }
}

function createTimeoutError(timeoutMs: number, url: string) {
  const error = new Error(`Request timed out after ${timeoutMs}ms`) as Error & {
    status?: number;
    statusCode?: number;
    code?: string;
    retryable?: boolean;
  };
  error.message = `Request timed out after ${timeoutMs}ms: ${url}`;
  error.name = 'HttpTimeoutError';
  error.status = 408;
  error.statusCode = 408;
  error.code = 'REQUEST_TIMEOUT';
  error.retryable = true;
  return error;
}

function getNetworkFailureMessage(error: any) {
  if (error?.code === 'REQUEST_TIMEOUT' || error?.name === 'HttpTimeoutError') {
    return 'Request timed out. Check your connection and try again.';
  }
  return 'Connection lost. Check your connection and try again.';
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number) {
  if (!timeoutMs || timeoutMs <= 0 || typeof AbortController === 'undefined') {
    return fetch(url, init);
  }

  const controller = new AbortController();
  const upstreamSignal = init.signal;
  let timedOut = false;

  if (upstreamSignal?.aborted) {
    controller.abort(upstreamSignal.reason);
  }

  const abortFromUpstream = () => controller.abort(upstreamSignal?.reason);
  upstreamSignal?.addEventListener('abort', abortFromUpstream, { once: true });

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (error: any) {
    if (timedOut && error?.name === 'AbortError') {
      throw createTimeoutError(timeoutMs, url);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    upstreamSignal?.removeEventListener('abort', abortFromUpstream);
  }
}

// readLocalToken is defined below with support for sessionStorage; keep a single definition

function readLocalAuth() {
  if (typeof window === 'undefined') return null as null | { accessToken?: string; refreshToken?: string; expiresAt?: number | null; savedAt?: number | null; store?: 'local' | 'session' };
  const key = 'storizone:auth:v1';
  try {
    const ls = window.localStorage.getItem(key);
    const ss = window.sessionStorage.getItem(key);
    const raw = ls ?? ss;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      accessToken: parsed?.accessToken ?? null,
      refreshToken: parsed?.refreshToken ?? null,
      expiresAt: typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : null,
      savedAt: typeof parsed?.savedAt === 'number' ? parsed.savedAt : null,
      store: ls ? 'local' : 'session',
    };
  } catch {
    return null;
  }
}

function readLocalLogoutAt(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('storizone:auth:logout:v1');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.at === 'number' ? parsed.at : null;
  } catch {
    return null;
  }
}

function isInvalidatedByLogout(auth: ReturnType<typeof readLocalAuth>): boolean {
  const logoutAt = readLocalLogoutAt();
  if (!logoutAt) return false;
  if (!auth) return false;
  return typeof auth.savedAt === 'number' && logoutAt > auth.savedAt;
}

function writeLocalAuth(tokens: { accessToken?: string | null; refreshToken?: string | null; expiresAt?: number | null; user?: unknown }) {
  if (typeof window === 'undefined') return;
  const key = 'storizone:auth:v1';
  try {
    const existing = readLocalAuth();
    const base = existing ?? { accessToken: null, refreshToken: null, expiresAt: null };
    const merged: any = { ...base, ...tokens, savedAt: Date.now() };
    const payload = JSON.stringify(merged);
    const target = (existing?.store ?? 'local') === 'local' ? window.localStorage : window.sessionStorage;
    target.setItem(key, payload);
  } catch {
    // ignore
  }
}

function readLocalToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('storizone:auth:v1') ?? window.sessionStorage.getItem('storizone:auth:v1');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isInvalidatedByLogout({
      accessToken: parsed?.accessToken ?? null,
      refreshToken: parsed?.refreshToken ?? null,
      expiresAt: typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : null,
      savedAt: typeof parsed?.savedAt === 'number' ? parsed.savedAt : null,
      store: window.localStorage.getItem('storizone:auth:v1') ? 'local' : 'session',
    })) return null;
    if (!parsed?.accessToken) return null;
    const exp = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : null;
    if (exp && Date.now() >= exp) return null;
    return parsed.accessToken as string;
  } catch {
    return null;
  }
}

function readLocalRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('storizone:auth:v1') ?? window.sessionStorage.getItem('storizone:auth:v1');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isInvalidatedByLogout({
      accessToken: parsed?.accessToken ?? null,
      refreshToken: parsed?.refreshToken ?? null,
      expiresAt: typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : null,
      savedAt: typeof parsed?.savedAt === 'number' ? parsed.savedAt : null,
      store: window.localStorage.getItem('storizone:auth:v1') ? 'local' : 'session',
    })) return null;
    return parsed?.refreshToken ?? null;
  } catch {
    return null;
  }
}

function randHex(bytes: number) {
  const arr = new Uint8Array(bytes);
  if (typeof crypto !== 'undefined' && (crypto as any).getRandomValues) (crypto as any).getRandomValues(arr);
  else for (let i = 0; i < bytes; i++) arr[i] = Math.floor(Math.random() * 256);
  return [...arr]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function generateTraceContext() {
  const traceId = randHex(16); // 16 bytes = 32 hex
  const spanId = randHex(8); // 8 bytes = 16 hex
  const traceparent = `00-${traceId}-${spanId}-01`;
  const requestId = `req_${Date.now().toString(36)}_${randHex(4)}`;
  return { traceId, spanId, traceparent, requestId } as const;
}

function waitForRefresh<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(signal.reason);
    signal.addEventListener('abort', abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
    if (signal.aborted) abort();
  });
}

export function createClient(options: CreateClientOptions) {
  // State belongs to this authentication context, never to the SDK module (SSR
  // requests and the merchant/customer/platform apps can use different clients).
  const cache = new Map<string, { etag: string; body: unknown }>();
  let authenticationContext: string | undefined;
  let cacheGeneration = 0;
  let refreshingPromise: ReturnType<typeof doRefresh> | null = null;
  const {
    baseUrl,
    getToken,
    getTenantId,
    getRefreshToken,
    disableLocalAuthFallback = false,
    onTokensRefreshed,
    onUnauthorized,
    refreshPath = '/auth/refresh',
    timeoutMs = readDefaultTimeoutMs()
  } = options;

  const getBaseUrl = () => {
    return (typeof baseUrl === 'function' ? baseUrl() : baseUrl) || '/api';
  };

  async function doRefresh(tenantId?: string | null): Promise<{ accessToken: string; refreshToken?: string | null; expiresAt?: number | null; user?: unknown } | null> {
    const rt = (await maybe(getRefreshToken)) ?? (disableLocalAuthFallback ? null : readLocalRefreshToken());
    if (!rt) return null;
    try {
      const url = makeUrl(getBaseUrl(), normalizePath(refreshPath));
      const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
      if (tenantId) headers[isUuid(tenantId) ? 'x-tenant-id' : 'x-store-slug'] = tenantId;
      const res = await fetchWithTimeout(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ refreshToken: rt }),
      }, timeoutMs);
      if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);
      const data: any = await res.json();
      if (typeof data?.accessToken !== 'string' || !data.accessToken.trim()) {
        throw new Error('Refresh returned no access token');
      }
      const parsedExpiry = typeof data?.expiresAt === 'string' ? Date.parse(data.expiresAt) : NaN;
      const expiresAt = typeof data?.expiresAt === 'number' ? data.expiresAt
        : Number.isFinite(parsedExpiry) ? parsedExpiry
        : typeof data?.expiresIn === 'number' ? Date.now() + data.expiresIn * 1000 : null;
      const tokens = { accessToken: data?.accessToken as string, refreshToken: data?.refreshToken ?? rt, expiresAt, user: data?.user };
      if (!disableLocalAuthFallback) writeLocalAuth(tokens);
      try { onTokensRefreshed?.(tokens); } catch { }
      // notify app via browser event
      try { if (!disableLocalAuthFallback && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('auth:tokens-refreshed', { detail: tokens })); } catch { }
      return tokens;
    } catch (error) {
      throw error;
    }
  }

  function notifyHttpError(detail: { method: Method; url: string; status?: number; statusText?: string; message?: string; requestId?: string; correlationId?: string; traceparent?: string; tenantId?: string | null; timestamp?: string; retryable?: boolean }) {
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('http:error', { detail }));
      }
    } catch { }
  }

  async function request<T>(method: Method, path: string, body?: any, options?: RequestOptions, refreshedToken?: string): Promise<T> {
    options?.signal?.throwIfAborted();
    const normalizedPath = normalizePath(path);
    const url = makeUrl(getBaseUrl(), normalizedPath);
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      ...options?.headers,
    };
    const { traceparent, requestId } = generateTraceContext();
    headers['traceparent'] = traceparent;
    headers['x-request-id'] = requestId;

    const localAuth = disableLocalAuthFallback ? null : readLocalAuth();
    const token = refreshedToken ?? (await maybe(getToken)) ?? (disableLocalAuthFallback ? null : readLocalToken());
    if (token && isInvalidatedByLogout(localAuth)) {
      if (onUnauthorized) onUnauthorized('manual', 'logout-broadcast-check');
      else dispatchAuthLogout('manual', 'logout-broadcast-check');
      throw createUnauthorizedError('Session signed out.');
    }
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const tenantId = options?.tenantId ?? await maybe(getTenantId);
    if (tenantId) {
      if (isUuid(tenantId)) headers['x-tenant-id'] = tenantId;
      else headers['x-store-slug'] = tenantId;
    }

    options?.signal?.throwIfAborted();
    const normalizedHeaders = new Headers(headers);
    const credentialHeaders = ['authorization', 'cookie', 'proxy-authorization', 'x-api-key'];
    const activeAuthenticationContext = JSON.stringify(credentialHeaders.map((name) => normalizedHeaders.get(name)));
    if (authenticationContext !== activeAuthenticationContext) {
      authenticationContext = activeAuthenticationContext;
      cache.clear();
      cacheGeneration++;
    }
    const generation = cacheGeneration;
    // Include caller-provided variants (locale, currency, store, etc.), while
    // excluding request-specific diagnostics and credentials from stored keys.
    const variants = [...normalizedHeaders.entries()].filter(([name]) =>
      ![...credentialHeaders, 'traceparent', 'x-request-id', 'if-none-match'].includes(name));
    const cacheKey = JSON.stringify([url, variants]);
    const requestNoStore = /(?:^|,)\s*no-store\b/i.test(normalizedHeaders.get('Cache-Control') || '');
    const cached = method === 'GET' && !requestNoStore ? cache.get(cacheKey) : undefined;
    if (cached && !normalizedHeaders.has('If-None-Match')) headers['If-None-Match'] = cached.etag;

    const init: RequestInit = { method, headers, credentials: 'include', signal: options?.signal };
    const hasBody = body !== undefined && body !== null;
    const canIncludeBody = method !== 'GET' && method !== 'HEAD';
    const isForm =
      (typeof FormData !== 'undefined' && body instanceof FormData) ||
      (typeof Blob !== 'undefined' && body instanceof Blob) ||
      body instanceof ArrayBuffer ||
      ArrayBuffer.isView(body) ||
      body instanceof URLSearchParams;
    if (hasBody && canIncludeBody) {
      if (isForm) {
        init.body = body as any;
      } else {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
    }

    let res: Response;
    try {
      res = await fetchWithTimeout(url, init, options?.timeoutMs ?? timeoutMs);
    } catch (error: any) {
      if (options?.signal?.aborted) throw error;
      const timestamp = new Date().toISOString();
      try {
        error.method = error.method || method;
        error.url = error.url || url;
        error.requestId = error.requestId || requestId;
        error.correlationId = error.correlationId || requestId;
        error.traceparent = error.traceparent || traceparent;
        error.tenantId = error.tenantId || tenantId || undefined;
        error.timestamp = error.timestamp || timestamp;
      } catch { }
      notifyHttpError({ method, url, status: error?.status || 0, statusText: 'NetworkError', message: getNetworkFailureMessage(error), requestId, correlationId: error?.correlationId || requestId, traceparent, tenantId, timestamp, retryable: error?.retryable });
      throw error;
    }

    if (res.status === 304) {
      const validator = new Headers(headers).get('If-None-Match');
      if (cached && validator === cached.etag && generation === cacheGeneration) {
        if (/(?:^|,)\s*no-store\b/i.test(res.headers.get('Cache-Control') || '') || res.headers.get('Vary')?.trim() === '*') cache.delete(cacheKey);
        return cached.body as T;
      }
      throw new HttpError(304, res.statusText, 'No matching cached representation', { method, url, requestId, traceparent });
    }

    const isAuthFlowRequest =
      typeof normalizedPath === 'string' &&
      /\/auth\/(login|refresh|register|forgot-password|reset-password|logout)(?:\/|$)/i.test(normalizedPath);

    if (res.status === 401 && !isAuthFlowRequest) {
      if (!options?.retried) {
        if (!refreshingPromise) refreshingPromise = doRefresh(tenantId).finally(() => { refreshingPromise = null; });
        let refreshed: Awaited<ReturnType<typeof doRefresh>>;
        try {
          refreshed = await waitForRefresh(refreshingPromise, options?.signal);
          if (!refreshed?.accessToken) throw createUnauthorizedError();
        } catch (error) {
          if (options?.signal?.aborted) throw error;
          if (onUnauthorized) onUnauthorized('session-expired', 'refresh-failed');
          else dispatchAuthLogout('session-expired', 'refresh-failed');
          throw createUnauthorizedError();
        }
        // Failures from the retried operation retain their real status. Pin the
        // tenant even if the active store changes while refresh is in progress.
        return request<T>(method, path, body, { ...options, tenantId: tenantId ?? '', retried: true }, refreshed.accessToken);
      }
      if (onUnauthorized) onUnauthorized('session-expired', 'retry-unauthorized');
      else dispatchAuthLogout('session-expired', 'retry-unauthorized');
      throw createUnauthorizedError();
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const timestamp = new Date().toISOString();
      let problem: any = null;
      try {
        problem = text ? JSON.parse(text) : null;
      } catch {
        problem = null;
      }
      const correlationId = res.headers.get('x-correlation-id') || res.headers.get('x-request-id')
        || (typeof problem?.correlationId === 'string' ? problem.correlationId : null) || requestId;
      const message = typeof problem?.message === 'string'
        ? problem.message
        : typeof problem?.detail === 'string'
          ? problem.detail
          : 'Server error. Please try again in a moment.';
      const retryable = typeof problem?.retryable === 'boolean' ? problem.retryable : undefined;
      // Only surface toast-worthy events for 5xx and hard failures
      if (res.status >= 500) {
        notifyHttpError({ method, url, status: res.status, statusText: res.statusText, message, requestId, correlationId, traceparent, tenantId, timestamp, retryable });
      }
      throw new HttpError(res.status, res.statusText, text, {
        method,
        url,
        requestId,
        correlationId,
        traceparent,
        timestamp,
      });
    }

    // Try JSON, else as text
    const contentType = (res.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
    const data = res.status === 204 || res.status === 205 ? undefined
      : contentType === 'application/json' || contentType.endsWith('+json') ? await res.json() : await res.text();
    if (method !== 'GET' && method !== 'HEAD') {
      // A mutation can change related list/detail responses. Incrementing the
      // generation also prevents an older in-flight GET from repopulating them.
      cache.clear();
      cacheGeneration++;
    } else if (method === 'GET' && generation === cacheGeneration) {
      const etag = res.headers.get('ETag');
      cache.delete(cacheKey);
      if (etag && !requestNoStore && !/(?:^|,)\s*no-store\b/i.test(res.headers.get('Cache-Control') || '')
        && res.headers.get('Vary')?.trim() !== '*') {
        if (cache.size >= 100) cache.delete(cache.keys().next().value!);
        cache.set(cacheKey, { etag, body: data });
      }
    }
    return data as T;
  }

  return {
    get: <T = unknown>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
    post: <T = unknown>(path: string, json?: Json | FormData, options?: RequestOptions) => request<T>('POST', path, json, options),
    patch: <T = unknown>(path: string, json?: Json, options?: RequestOptions) => request<T>('PATCH', path, json, options),
    put: <T = unknown>(path: string, json?: Json, options?: RequestOptions) => request<T>('PUT', path, json, options),
    delete: <T = unknown>(path: string, json?: Json, options?: RequestOptions) => request<T>('DELETE', path, json, options),
  };
}

export type HttpClient = ReturnType<typeof createClient>;

// Feature flag for stub fallbacks across SDK
export function isStubEnabled(): boolean {
  try {
    // Browser toggles
    if (typeof window !== 'undefined') {
      const w = window as any;
      if (w && w.__STORIZONE_USE_STUBS__ !== undefined) return !!w.__STORIZONE_USE_STUBS__;
      // localStorage key
      const v = window.localStorage.getItem('storizone:use_stubs');
      if (v) return v === '1' || v === 'true';
    }
  } catch { }
  // If the user is logged in with a valid token, don't use stubs - prefer real API
  try {
    const token = readLocalToken();
    if (token) return false;
  } catch { }
  try {
    // Environment variables for build/runtime
    const env: any =
      typeof globalThis !== 'undefined' && (globalThis as any)?.process?.env ? (globalThis as any).process.env : {};
    const val = env.USE_STUBS || env.NEXT_PUBLIC_USE_STUBS || env.VITE_USE_STUBS;
    if (val !== undefined) return String(val) === '1' || String(val).toLowerCase() === 'true';

    // Vite / ESM
    try {
      if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
        const val = (import.meta as any).env.VITE_USE_STUBS;
        if (val !== undefined) return String(val) === '1' || String(val).toLowerCase() === 'true';
      }
    } catch { }

  } catch { }
  return false;
}

